import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { Button, Card, Chip, Input, Modal, Select } from '@/ui/primitives';
import { fetchCurrentUser } from '@/lib/auth';
import {
  useMipCatalogo, useCreateMipItem, useUpdateMipItem, useDeleteMipItem, useUpdateMipComposto,
  type MipItemInput,
} from '../api';
import type { MipItem, MipComposto } from '../visita/schemas';

const CATEGORIA_OPTS = [
  { value: 'praga', label: 'Praga' },
  { value: 'doenca', label: 'Doença' },
  { value: 'daninha', label: 'Daninha' },
  { value: 'nutricao', label: 'Nutrição' },
  { value: 'outra', label: 'Outra' },
];
const UNIDADE_SUGEST = ['pano de batida', 'm²', 'planta', '%', 'ponto'];
const FASE_OPTS = [
  { value: 'adulto', label: 'Adultos' },
  { value: 'ninfa', label: 'Ninfas' },
  { value: 'ovo', label: 'Ovos' },
  { value: 'larva', label: 'Larvas' },
];

function fmtLimiar(n: number | null) {
  return n == null ? '—' : String(n);
}

export function CatalogoMipView() {
  const { data: me } = useQuery({ queryKey: ['auth', 'me'], queryFn: ({ signal }) => fetchCurrentUser(signal) });
  const { data: catalogo, isLoading } = useMipCatalogo();
  const del = useDeleteMipItem();
  const [editItem, setEditItem] = useState<MipItem | 'new' | null>(null);
  const [editComposto, setEditComposto] = useState<MipComposto | null>(null);

  if (me && me.role !== 'admin') {
    return <Card className="text-sm text-text-muted">O catálogo MIP é editável apenas por administradores.</Card>;
  }
  if (isLoading || !catalogo) {
    return <p className="text-sm text-text-muted">Carregando catálogo…</p>;
  }

  const porCategoria = CATEGORIA_OPTS.map((c) => ({
    cat: c.value,
    label: c.label,
    itens: catalogo.itens.filter((i) => i.categoria === c.value),
  })).filter((g) => g.itens.length > 0);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-text-secondary">
          Itens monitorados e indicadores compostos, com limiares de atenção e ação. Editar um limiar muda a severidade nos relatórios.
        </p>
        <Button variant="primary" onClick={() => setEditItem('new')}>
          <Plus size={14} strokeWidth={2} />
          Novo item
        </Button>
      </div>

      {porCategoria.map((g) => (
        <Card key={g.cat} className="overflow-hidden p-0">
          <div className="border-b border-border px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-text-muted">
            {g.label} <span className="text-text-2">· {g.itens.length}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-text-muted">
                  <th className="py-2 pl-4 pr-2 font-medium">Item</th>
                  <th className="px-2 py-2 font-medium">Unidade</th>
                  <th className="px-2 py-2 font-medium">Fases</th>
                  <th className="px-2 py-2 font-medium text-right">Atenção</th>
                  <th className="px-2 py-2 font-medium text-right">Ação</th>
                  <th className="py-2 pl-2 pr-4 font-medium text-right">—</th>
                </tr>
              </thead>
              <tbody>
                {g.itens.map((it) => (
                  <tr key={it.id} className="border-b border-border last:border-0">
                    <td className="py-2 pl-4 pr-2">
                      <div className="text-text">{it.nome}</div>
                      {it.nome_cientifico && <div className="text-[11px] italic text-text-muted">{it.nome_cientifico}</div>}
                    </td>
                    <td className="px-2 py-2 text-text-2">{it.unidade}</td>
                    <td className="px-2 py-2">
                      <span className="flex flex-wrap gap-1">
                        {it.fases.length
                          ? it.fases.map((f) => <Chip key={f} tone="neutral" size="sm">{FASE_OPTS.find((o) => o.value === f)?.label ?? f}</Chip>)
                          : <span className="text-text-muted">—</span>}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums text-text-2">{fmtLimiar(it.limiar_atencao)}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-text">{fmtLimiar(it.limiar_acao)}</td>
                    <td className="py-2 pl-2 pr-4 text-right">
                      <span className="inline-flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => setEditItem(it)} aria-label="editar"><Pencil size={13} /></Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { if (confirm(`Remover "${it.nome}" do catálogo?`)) del.mutate(it.id); }}
                          aria-label="remover"
                        >
                          <Trash2 size={13} className="text-danger" />
                        </Button>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ))}

      {/* Indicadores compostos */}
      {catalogo.compostos.length > 0 && (
        <Card className="overflow-hidden p-0">
          <div className="border-b border-border px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-text-muted">
            Indicadores compostos <span className="text-text-2">· {catalogo.compostos.length}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-text-muted">
                  <th className="py-2 pl-4 pr-2 font-medium">Indicador</th>
                  <th className="px-2 py-2 font-medium">Fase</th>
                  <th className="px-2 py-2 font-medium text-right">Itens</th>
                  <th className="px-2 py-2 font-medium text-right">Ação</th>
                  <th className="py-2 pl-2 pr-4 font-medium text-right">—</th>
                </tr>
              </thead>
              <tbody>
                {catalogo.compostos.map((c) => (
                  <tr key={c.id} className="border-b border-border last:border-0">
                    <td className="py-2 pl-4 pr-2 text-text">{c.nome}</td>
                    <td className="px-2 py-2 text-text-2">{c.fase ? (FASE_OPTS.find((o) => o.value === c.fase)?.label ?? c.fase) : 'todas'}</td>
                    <td className="px-2 py-2 text-right text-text-2">{c.item_ids.length}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-text">{fmtLimiar(c.limiar_acao)}</td>
                    <td className="py-2 pl-2 pr-4 text-right">
                      <Button variant="ghost" size="sm" onClick={() => setEditComposto(c)} aria-label="editar"><Pencil size={13} /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <ItemFormModal item={editItem} onClose={() => setEditItem(null)} />
      <CompostoFormModal composto={editComposto} onClose={() => setEditComposto(null)} />
    </div>
  );
}

function numOrNull(s: string): number | null {
  if (s.trim() === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function ItemFormModal({ item, onClose }: { item: MipItem | 'new' | null; onClose: () => void }) {
  const create = useCreateMipItem();
  const update = useUpdateMipItem();
  const isNew = item === 'new';
  const cur = isNew ? null : item;

  // Estado do formulário derivado do item; keyed remount via `key` no Modal.
  const [categoria, setCategoria] = useState(cur?.categoria ?? 'praga');
  const [nome, setNome] = useState(cur?.nome ?? '');
  const [sci, setSci] = useState(cur?.nome_cientifico ?? '');
  const [unidade, setUnidade] = useState(cur?.unidade ?? 'pano de batida');
  const [fases, setFases] = useState<string[]>(cur?.fases ?? []);
  const [atencao, setAtencao] = useState(cur?.limiar_atencao != null ? String(cur.limiar_atencao) : '');
  const [acao, setAcao] = useState(cur?.limiar_acao != null ? String(cur.limiar_acao) : '');
  const [erro, setErro] = useState<string | null>(null);

  function toggleFase(f: string) {
    setFases((prev) => (prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]));
  }

  async function salvar() {
    if (!nome.trim()) { setErro('Informe o nome.'); return; }
    const body: MipItemInput = {
      categoria,
      nome: nome.trim(),
      nome_cientifico: sci.trim() || null,
      unidade: unidade.trim() || 'ponto',
      fases,
      limiar_atencao: numOrNull(atencao),
      limiar_acao: numOrNull(acao),
    };
    try {
      if (isNew) await create.mutateAsync(body);
      else if (cur) await update.mutateAsync({ id: cur.id, body });
      onClose();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao salvar');
    }
  }

  const saving = create.isPending || update.isPending;

  return (
    <Modal
      key={isNew ? 'new' : cur?.id ?? 'none'}
      open={item != null}
      onOpenChange={(v) => !v && onClose()}
      title={isNew ? 'Novo item do catálogo' : `Editar · ${cur?.nome ?? ''}`}
    >
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-text-2">Categoria</span>
            <Select value={categoria} onChange={(e) => setCategoria(e.target.value)}>
              {CATEGORIA_OPTS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </Select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-text-2">Unidade</span>
            <Input list="mip-unidades" value={unidade} onChange={(e) => setUnidade(e.target.value)} placeholder="pano de batida" />
            <datalist id="mip-unidades">{UNIDADE_SUGEST.map((u) => <option key={u} value={u} />)}</datalist>
          </label>
        </div>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-text-2">Nome</span>
          <Input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Percevejo-marrom" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-text-2">Nome científico (opcional)</span>
          <Input value={sci} onChange={(e) => setSci(e.target.value)} placeholder="Euschistus heros" />
        </label>
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-text-2">Fases monitoradas</span>
          <div className="flex flex-wrap gap-2">
            {FASE_OPTS.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => toggleFase(f.value)}
                className={
                  'rounded-full border px-3 h-7 text-xs transition-colors ' +
                  (fases.includes(f.value)
                    ? 'border-transparent bg-[var(--accent-soft)] text-accent'
                    : 'border-border bg-elevated text-text-2 hover:bg-surface2')
                }
              >
                {f.label}
              </button>
            ))}
          </div>
          <span className="text-[11px] text-text-muted">Sem fase = um valor por ponto (ex.: doença/daninha em %).</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-text-2">Limiar de atenção</span>
            <Input type="number" inputMode="decimal" value={atencao} onChange={(e) => setAtencao(e.target.value)} placeholder="—" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-text-2">Limiar de ação</span>
            <Input type="number" inputMode="decimal" value={acao} onChange={(e) => setAcao(e.target.value)} placeholder="—" />
          </label>
        </div>

        {erro && <p className="text-xs text-danger">{erro}</p>}

        <div className="flex justify-end gap-2 border-t border-border pt-3">
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button variant="primary" onClick={() => void salvar()} disabled={saving}>
            {saving ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function CompostoFormModal({ composto, onClose }: { composto: MipComposto | null; onClose: () => void }) {
  const update = useUpdateMipComposto();
  const [nome, setNome] = useState(composto?.nome ?? '');
  const [acao, setAcao] = useState(composto?.limiar_acao != null ? String(composto.limiar_acao) : '');
  const [erro, setErro] = useState<string | null>(null);

  async function salvar() {
    if (!composto) return;
    try {
      await update.mutateAsync({ id: composto.id, body: { nome: nome.trim() || composto.nome, limiar_acao: numOrNull(acao) } });
      onClose();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao salvar');
    }
  }

  return (
    <Modal
      key={composto?.id ?? 'none'}
      open={composto != null}
      onOpenChange={(v) => !v && onClose()}
      title={`Editar indicador · ${composto?.nome ?? ''}`}
    >
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-text-2">Nome</span>
          <Input value={nome} onChange={(e) => setNome(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-text-2">Limiar de ação</span>
          <Input type="number" inputMode="decimal" value={acao} onChange={(e) => setAcao(e.target.value)} placeholder="—" />
        </label>
        {erro && <p className="text-xs text-danger">{erro}</p>}
        <div className="flex justify-end gap-2 border-t border-border pt-3">
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button variant="primary" onClick={() => void salvar()} disabled={update.isPending}>
            {update.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
